import {
  colorBaseNeutral700,
  colorContentPlaceholder,
} from '@duxton-web/design-tokens/themes/light';
import {
  Button,
  Card,
  Col,
  Flex,
  Row,
  Tabs,
  Tag,
  Typography,
} from '@duxton-web/ui';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { SendUniversal } from '@duxton-web/icons';

import SmartCampaignPCIPreview from 'omega-rtc/components/form/ucd/SmartCampaignPCIPreview';
import SmartCampaignSendPreview from 'omega-rtc/components/form/ucd/SmartCampaignSendPreview';
import { SMART_CAMPAIGN_CHANNELS } from 'omega-rtc/constants/ucd';
import supportedLanguages from 'omega-rtc/data/languages.json';

import { showControlGroup } from 'omega-rtc/config';

import {
  generatePersonalAttributesHTML,
  getLanguageNameFromCode,
} from 'omega-rtc/utils/smart-campaign-utils';

import './SmartCampaignContentVariants.scss';

type PreviewContainerProps = {
  channel: string;
  isManualMode: boolean;
  isPreviewMode: boolean;
  primaryLanguage: string;
  pciMetaData?: PCIMetaData;
  contentVariants: SelectedVariant[];
};

type SelectedVariant = {
  title: number;
  message: number;
  isControlGroup: boolean;
  variantNumber: number;
};

type PCIMetaData = {
  title?: string;
  message?: string;
  subtitle?: string;
  category?: string;
  buttonText?: string;
  buttonLink?: string;
  coverImageURL?: string;
};

type SelectedVariantsForEachLanguages = Record<string,
  { contentVariants: SelectedVariant[]; pciMetaData?: PCIMetaData }>;

type SmartCampaignLanguageBasedPreviewProps = {
  channel: string;
  isPreviewMode: boolean;
  isManualMode: boolean;
  primaryLanguage: string;
  selectedLanguages: string[];
  metaDataForEachLanguages: SelectedVariantsForEachLanguages;
};

const SmartCampaignLanguageBasedPreview = ({
  channel,
  isPreviewMode,
  selectedLanguages,
  metaDataForEachLanguages,
  isManualMode,
  primaryLanguage,
}: SmartCampaignLanguageBasedPreviewProps) => {
  const tabItems = useMemo(() => {
    if (!selectedLanguages) {
      return;
    }

    return selectedLanguages.map((languageCode) => ({
      key: supportedLanguages[languageCode].code,
      label: supportedLanguages[languageCode].label,
      children: (
        <PreviewContainer
          isManualMode={isManualMode}
          isPreviewMode={isPreviewMode}
          language={languageCode}
          contentVariants={
            (metaDataForEachLanguages[languageCode] || {}).contentVariants
          }
          pciMetaData={
            (metaDataForEachLanguages[languageCode] || {}).pciMetaData
          }
          channel={channel}
          key={languageCode}
          primaryLanguage={primaryLanguage}
        />
      ),
    }));
  }, [selectedLanguages, metaDataForEachLanguages]);

  return <Tabs type="pill" tabPosition="top" items={tabItems} />;
};

const PreviewContainer = ({
  language,
  channel,
  pciMetaData,
  isPreviewMode,
  isManualMode,
  contentVariants,
  primaryLanguage,
}: PreviewContainerProps) => {
  const [sendPreviewModalOpen, setSendPreviewModalOpen] = useState(false);

  return (
    <div>
      <Row justify="space-between" align={'middle'}>
        <Typography.Body level={2} strong>
          Selected Content Variants
        </Typography.Body>
        {isPreviewMode && (
          <Button
            icon={<SendUniversal />}
            onClick={() => setSendPreviewModalOpen(true)}
          >
            Send preview message
          </Button>
        )}
      </Row>
      <Row
        gutter={[20, 20]}
        style={{
          marginTop: 10,
          marginBottom: 8,
        }}
      >
        {contentVariants && contentVariants.length > 0 ? (
          contentVariants.map((variant) => (
            <Col span={12} key={variant.variantNumber}>
              <Card
                key={variant.variantNumber}
                styles={{
                  body: { padding: 0, paddingLeft: 16, paddingRight: 16 },
                  header: {
                    fontSize: 12,
                    fontWeight: 400,
                    borderBottom: 0,
                    minHeight: 'auto',
                    lineHeight: '16px',
                    textShadow: 'none',
                    padding: '0px 16px',
                    marginBottom: 12,
                    color: colorBaseNeutral700,
                  },
                }}
                style={{ paddingTop: 20, paddingBottom: 20 }}
                title={`variant ${variant.variantNumber} ${showControlGroup && variant.isControlGroup ? '(Control)' : ''}`}
              >
                <Flex vertical gap={8} style={{ whiteSpace: 'pre-wrap' }}>
                  <Typography.Body level={2} strong>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: generatePersonalAttributesHTML(
                          variant.title || '-',
                        ),
                      }}
                    />
                  </Typography.Body>

                  <Typography.Body level={3} style={{ textShadow: 'none' }}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: generatePersonalAttributesHTML(
                          variant.message || '-',
                        ),
                      }}
                    />
                  </Typography.Body>
                </Flex>
                {primaryLanguage === language && !isManualMode && (
                  <Tag
                    color="blue"
                    style={{ width: 'fit-content', marginTop: 12 }}
                  >
                    {_.capitalize(
                      variant.slang ||
                        getLanguageNameFromCode({ languageCode: language }), // Value of slang will be undefined, if their is no slang for variant. if so, we will show the language name.
                    )}
                  </Tag>
                )}
              </Card>
            </Col>
          ))
        ) : (
          <Typography.Body level={2} style={{ color: colorContentPlaceholder }}>
            No variant is selected
          </Typography.Body>
        )}
      </Row>

      {channel === SMART_CAMPAIGN_CHANNELS.pci && (
        <SmartCampaignPCIPreview pciMetaData={pciMetaData || {}} />
      )}
      <SmartCampaignSendPreview
        open={sendPreviewModalOpen}
        onClose={() => setSendPreviewModalOpen(false)}
        isInConfirmationPage
        selectedVariants={contentVariants}
        language={language}
      />
    </div>
  );
};

export default SmartCampaignLanguageBasedPreview;
